"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Mail, MapPin, Clock, Phone, ArrowUpRight, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2 } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
interface ContactChannel {
  label: string;
  value: string;
  detail: string;
  href?: string;
  type: "email" | "tel" | "text";
}
const contactChannels: ContactChannel[] = [{
  label: "Editorial & Research Desk",
  value: "info@minfomedia.com",
  detail: "Direct inquiries regarding reports, methodologies, and submissions",
  href: "mailto:info@minfomedia.com",
  type: "email"
}, {
  label: "Telephone & Media Line",
  value: "(555) 123-4567",
  detail: "Available weekdays for analyst briefings and press coverage",
  href: "tel:5551234567",
  type: "tel"
}, {
  label: "Headquarters & Bureau",
  value: "123 Main Street, Springfield, USA",
  detail: "Editorial and analytical operations center",
  type: "text"
}, {
  label: "Operating Hours",
  value: "Monday – Friday: 9am – 6pm",
  detail: "Standard Eastern business hours with 24-48h response cadence",
  type: "text"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-muted/40 rounded-md border border-border">
        <CheckCircle2 className="size-10 text-primary mb-3" />
        <h3 className="font-serif text-xl font-semibold text-foreground mb-2">
          Message received
        </h3>
        <p className="text-muted-foreground text-sm max-w-md mb-6">
          {successMessage || "Thank you for reaching out. Our editorial team will review your inquiry within one business day."}
        </p>
        <Button onClick={resetForm} variant="outline" size="sm" className="rounded-sm">
          Send another inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Please enter at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-foreground">
              Your Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Full name" className="bg-background rounded-sm border-input px-3 py-2 text-foreground focus-visible:border-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="email" rules={{
        required: "Business email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please provide a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-foreground">
                Business Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="name@company.com" className="bg-background rounded-sm border-input px-3 py-2 text-foreground focus-visible:border-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" rules={{
        required: "Company name is required",
        minLength: {
          value: 2,
          message: "Please provide your company name"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-foreground">
                Company
              </FormLabel>
              <FormControl>
                <Input placeholder="Company name" className="bg-background rounded-sm border-input px-3 py-2 text-foreground focus-visible:border-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please write at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-foreground">
              Message
            </FormLabel>
            <FormControl>
              <Textarea placeholder="How can our editorial team assist you?" rows={4} className="bg-background rounded-sm border-input px-3 py-2 text-foreground focus-visible:border-ring resize-y min-h-[110px]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-sm text-destructive text-sm">
          <AlertCircle className="size-4 shrink-0 mt-0.5" />
          <span>{submitError}</span>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-sm bg-primary text-primary-foreground font-medium py-3 h-auto transition-colors hover:bg-primary/90">
        {isSubmitting ? <>
            <Loader2 className="animate-spin size-4" />
            <span>Sending inquiry...</span>
          </> : "Send Inquiry"}
      </Button>

      <p className="text-[12px] text-muted-foreground text-center pt-1">
        We adhere strictly to confidentiality and never share your correspondence or credentials.
      </p>
    </Form>;
}
export default function Contact() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="contact" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Section Masthead Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
              Editorial Communications
            </span>
            <span className="h-px flex-1 bg-border max-w-[80px]" />
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground max-w-3xl">
            Get in touch with our editorial desk
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mt-4 leading-relaxed">
            Direct channels for editorial contributions, research methodology questions, and institutional partnerships.
          </p>
        </motion.div>

        {/* Two-Column Grid: Direct Channels & Form */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Direct Contacts */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={containerVariants} className="lg:col-span-5 space-y-6">
            <div className="border-b-2 border-primary pb-3 mb-6">
              <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
                Bureau & Contact Details
              </h3>
            </div>

            <div className="space-y-4">
              {contactChannels.map((channel, i) => <div key={channel.label} data-index={i} className="p-5 rounded-md bg-card border border-border transition-[border-color,box-shadow] hover:border-primary/40 shadow-xs">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        {channel.type === "email" && <Mail className="size-4 shrink-0 text-primary" />}
                        {channel.type === "tel" && <Phone className="size-4 shrink-0 text-primary" />}
                        {channel.label.includes("Headquarters") && <MapPin className="size-4 shrink-0 text-primary" />}
                        {channel.label.includes("Hours") && <Clock className="size-4 shrink-0 text-primary" />}
                        <span className="text-xs font-semibold uppercase tracking-wider">
                          {channel.label}
                        </span>
                      </div>

                      {channel.href ? <Link href={channel.href} className="inline-flex items-center gap-1.5 font-sans font-medium text-foreground text-base hover:text-primary transition-colors group">
                          <span>{channel.value}</span>
                          <ArrowUpRight className="size-3.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </Link> : <p className="font-sans font-medium text-foreground text-base">
                          {channel.value}
                        </p>}

                      <p className="text-xs text-muted-foreground pt-1 leading-relaxed">
                        {channel.detail}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Editorial Standard Note */}
            <div className="p-4 rounded-md bg-secondary/50 border border-border text-xs text-secondary-foreground space-y-1.5">
              <div className="font-semibold text-foreground">Editorial Transparency Notice</div>
              <p className="leading-relaxed">
                All editorial submissions and report requests undergo peer-review. MinfoMedia maintains strict independence and does not accept paid promotional placements in our research index.
              </p>
            </div>
          </motion.div>

          {/* Right Column: Inquiries Form */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={containerVariants} className="lg:col-span-7">
            <Card className="rounded-md border-border shadow-sm bg-card">
              <CardHeader className="border-b border-border pb-5">
                <CardTitle className="font-serif text-2xl font-semibold text-foreground">
                  Editorial & General Inquiries
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm mt-1">
                  Submit your research query, press inquiry, or collaboration request directly to the desk.
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-6">
                <FormProvider formId="about-us_contact_form" sectionName="about-us-contact-section" defaultValues={{
                name: "",
                email: "",
                company: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                company: {
                  required: true,
                  minLength: 2
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Send Inquiry" successMessage="Your message has been dispatched to our editorial department." errorMessage="Unable to send your inquiry at this moment. Please email info@minfomedia.com directly.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}